import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { UiIconComponent } from '../icon/icon.component';

export type UiIconBadgeTone = 'primary' | 'accent' | 'neutral' | 'danger' | 'warn';
export type UiIconBadgeSize = 'sm' | 'md';

/**
 * Small tinted container wrapping an icon — used for list-row leading glyphs,
 * summary markers, and status chips. `tone` reuses the button intent vocabulary
 * (primary | accent | neutral | danger) plus `warn` for financial caution
 * states (over budget, needs review).
 */
@Component({
  selector: 'app-ui-icon-badge',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [UiIconComponent],
  template: `
    <span [class]="containerClasses()">
      <app-ui-icon [name]="icon()" [set]="set()" [class]="iconClasses()" />
    </span>
  `,
})
export class UiIconBadgeComponent {
  readonly icon = input.required<string>();
  readonly set = input<'app' | 'budget'>('app');
  readonly tone = input<UiIconBadgeTone>('neutral');
  readonly size = input<UiIconBadgeSize>('md');

  protected readonly containerClasses = computed(
    () => `inline-flex shrink-0 items-center justify-center rounded-lg ${SIZE_CLASSES[this.size()]} ${TONE_CLASSES[this.tone()]}`,
  );
  protected readonly iconClasses = computed(() => ICON_SIZE_CLASSES[this.size()]);
}

const TONE_CLASSES: Record<UiIconBadgeTone, string> = {
  primary: 'bg-brand-primary/10 text-brand-primary',
  accent: 'bg-brand-accent/10 text-brand-accent',
  neutral: 'bg-surface-muted text-text-muted',
  danger: 'bg-status-danger/10 text-status-danger',
  warn: 'bg-status-warn/10 text-status-warn',
};

const SIZE_CLASSES: Record<UiIconBadgeSize, string> = {
  sm: 'h-8 w-8',
  md: 'h-10 w-10',
};

const ICON_SIZE_CLASSES: Record<UiIconBadgeSize, string> = {
  sm: 'h-6 w-6',
  md: 'h-8 w-8',
};
